import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PagesTab } from "@/components/site/PagesTab";
import { EmergentSetup } from "@/components/site/EmergentSetup";
import { GhlSetup } from "@/components/site/GhlSetup";
import { FormsTab } from "@/components/site/FormsTab";
import { SettingsTab } from "@/components/site/SettingsTab";

export default function SiteDetail() {
  const { siteId } = useParams();
  const [params, setParams] = useSearchParams();
  const [site, setSite] = useState(null);
  const tab = params.get("tab") || "pages";

  const load = useCallback(() => api.get(`/sites/${siteId}`).then((r) => setSite(r.data)).catch((e) => toast.error(errMsg(e))), [siteId]);
  useEffect(() => { load(); }, [load]);

  if (!site) return <div className="font-mono text-sm text-slate-500" data-testid="site-loading">Loading website…</div>;

  return (
    <div className="space-y-8">
      <div>
        <Link to="/" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900 transition-colors duration-150" data-testid="back-to-websites"><ArrowLeft className="h-3.5 w-3.5" /> Websites</Link>
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-3 mt-3">
          <div>
            <h1 className="font-display text-3xl font-extrabold tracking-tight" data-testid="site-title">{site.name}</h1>
            <div className="font-mono text-sm text-slate-500 mt-1">{site.domain}</div>
          </div>
          <a href={site.spanish_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 font-mono text-sm text-blue-600 hover:underline" data-testid="spanish-url-link">
            {site.spanish_url} <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </div>
      </div>
      <Tabs value={tab} onValueChange={(v) => setParams({ tab: v })}>
        <TabsList className="bg-transparent border-b border-slate-200 rounded-none w-full justify-start h-auto p-0 gap-6">
          {[["pages", "Review"], ["setup", "Install"], ...(site.platform === "ghl" ? [["forms", "Spanish forms"]] : []), ["settings", "Settings"]].map(([v, l]) => (
            <TabsTrigger key={v} value={v} data-testid={`site-tab-${v}`} className="rounded-none border-b-2 border-transparent data-[state=active]:border-slate-900 data-[state=active]:shadow-none data-[state=active]:bg-transparent px-0 pb-3 text-sm">{l}</TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="pages" className="pt-6"><PagesTab site={site} onChange={load} /></TabsContent>
        <TabsContent value="setup" className="pt-6">{site.platform === "ghl" ? <GhlSetup site={site} onChange={load} /> : <EmergentSetup site={site} />}</TabsContent>
        {site.platform === "ghl" && <TabsContent value="forms" className="pt-6"><FormsTab site={site} onChange={load} /></TabsContent>}
        <TabsContent value="settings" className="pt-6"><SettingsTab site={site} onChange={load} /></TabsContent>
      </Tabs>
    </div>
  );
}
